import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

interface PreviewProduct {
  asin: string; title: string | null; brand: string | null;
  imageUrl: string | null; salesRank: number | null;
  price: number | null; buyBoxPrice: number | null;
  rating: number | null; reviewCount: number | null;
  monthlySold: number | null; newOfferCount: number | null;
  rootCategory: number | null;
  tracked: { label: string; is_active: number } | null;
}

interface TokenStatus { tokensLeft: number; refillRate: number; refillIn: number }

const fmtNum = (n: number | null) => n != null ? new Intl.NumberFormat('en-US').format(n) : '—';
const fmtUsd = (n: number | null) => n != null ? `$${n.toFixed(2)}` : '—';

const DOMAIN_LABELS: Record<number, string> = {
  1: 'US', 2: 'UK', 3: 'DE', 4: 'FR', 5: 'JP', 6: 'CA', 8: 'IT', 9: 'ES', 10: 'IN', 11: 'MX',
};

type SearchMode = 'best-sellers' | 'product-finder' | 'seller';

export function NicheExplorer() {
  const [mode, setMode] = useState<SearchMode>('best-sellers');
  const [domainId, setDomainId] = useState(1);
  const [accountId, setAccountId] = useState('');
  const [, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PreviewProduct[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tokens, setTokens] = useState<TokenStatus | null>(null);
  const [error, setError] = useState('');

  // Best sellers
  const [categoryId, setCategoryId] = useState('');

  // Product finder
  const [bsrMax, setBsrMax] = useState('50000');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [reviewMin, setReviewMin] = useState('');
  const [monthlySoldMin, setMonthlySoldMin] = useState('');

  // Seller
  const [sellerId, setSellerId] = useState('');

  // Track label
  const [trackLabel, setTrackLabel] = useState<'competitor' | 'research'>('research');

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/accounts').then(accts => {
      setAccounts(accts);
      if (accts.length > 0) setAccountId(accts[0].id);
    }).catch(() => {});
    api.get<TokenStatus>('/keepa/explore/token-status').then(setTokens).catch(() => {});
  }, []);

  async function searchBestSellers() {
    if (!categoryId) return;
    setLoading(true); setError(''); setResults([]); setSelected(new Set());
    try {
      const bs = await api.post<{ asins: string[] }>('/keepa/explore/best-sellers', {
        categoryId: parseInt(categoryId), domainId, accountId: accountId || '_global',
      });
      if (bs.asins.length === 0) { setError('No best sellers found for this category'); setLoading(false); return; }
      const preview = await api.post<{ products: PreviewProduct[] }>('/keepa/explore/preview', {
        asins: bs.asins.slice(0, 50), domainId, accountId: accountId || '_global',
      });
      setResults(preview.products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    }
    setLoading(false);
    refreshTokens();
  }

  async function searchProductFinder() {
    setLoading(true); setError(''); setResults([]); setSelected(new Set());
    try {
      const req: Record<string, unknown> = { domainId, accountId: accountId || '_global', perPage: 50 };
      if (bsrMax) req.salesRankRange = [0, parseInt(bsrMax)];
      if (priceMin || priceMax) req.currentPriceRange = [parseInt(priceMin || '0') * 100, parseInt(priceMax || '999999') * 100];
      if (reviewMin) req.reviewCountRange = [parseInt(reviewMin), 999999];
      if (monthlySoldMin) req.monthlySoldRange = [parseInt(monthlySoldMin), 999999];
      if (categoryId) req.rootCategory = parseInt(categoryId);

      const found = await api.post<{ asins: string[]; total: number }>('/keepa/explore/product-finder', req);
      if (found.asins.length === 0) { setError(`No products match criteria (${found.total} total)`); setLoading(false); return; }

      const preview = await api.post<{ products: PreviewProduct[] }>('/keepa/explore/preview', {
        asins: found.asins.slice(0, 50), domainId, accountId: accountId || '_global',
      });
      setResults(preview.products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    }
    setLoading(false);
    refreshTokens();
  }

  async function searchSeller() {
    if (!sellerId) return;
    setLoading(true); setError(''); setResults([]); setSelected(new Set());
    try {
      const seller = await api.post<{ name: string; asins: string[]; asinCount: number }>(
        '/keepa/explore/seller-storefront', { sellerId, domainId, accountId: accountId || '_global' },
      );
      if (seller.asins.length === 0) { setError(`Seller has ${seller.asinCount} ASINs but storefront not accessible`); setLoading(false); return; }

      const preview = await api.post<{ products: PreviewProduct[] }>('/keepa/explore/preview', {
        asins: seller.asins.slice(0, 50), domainId, accountId: accountId || '_global',
      });
      setResults(preview.products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    }
    setLoading(false);
    refreshTokens();
  }

  function refreshTokens() {
    api.get<TokenStatus>('/keepa/explore/token-status').then(setTokens).catch(() => {});
  }

  function toggleSelect(asin: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(asin) ? next.delete(asin) : next.add(asin);
      return next;
    });
  }

  function selectAllUntracked() {
    setSelected(new Set(results.filter(p => !p.tracked?.is_active).map(p => p.asin)));
  }

  async function bulkTrack() {
    if (selected.size === 0 || !accountId) return;
    setLoading(true);
    try {
      await api.post('/keepa/explore/bulk-track', {
        asins: [...selected], accountId, domainId, label: trackLabel,
      });
      // Refresh preview to show tracked status
      const preview = await api.post<{ products: PreviewProduct[] }>('/keepa/explore/preview', {
        asins: results.map(p => p.asin), domainId, accountId: accountId || '_global',
      });
      setResults(preview.products);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Track failed');
    }
    setLoading(false);
  }

  const handleSearch = () => {
    if (mode === 'best-sellers') searchBestSellers();
    else if (mode === 'product-finder') searchProductFinder();
    else searchSeller();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Niche Explorer</h2>
          <p className="text-sm text-gray-500">Discover competitors and niche products via Keepa</p>
        </div>
        {tokens && (
          <div className="text-right">
            <p className="text-xs text-gray-400">Keepa Tokens</p>
            <p className={`text-lg font-bold ${tokens.tokensLeft < 100 ? 'text-red-600' : 'text-green-600'}`}>
              {fmtNum(tokens.tokensLeft)}
            </p>
            <p className="text-[10px] text-gray-400">{tokens.refillRate}/min refill</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1">
            {([
              { key: 'best-sellers' as const, label: 'Best Sellers' },
              { key: 'product-finder' as const, label: 'Product Finder' },
              { key: 'seller' as const, label: 'Seller Storefront' },
            ]).map(m => (
              <button key={m.key} onClick={() => setMode(m.key)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${
                  mode === m.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>{m.label}</button>
            ))}
          </div>
          <select value={domainId} onChange={e => setDomainId(parseInt(e.target.value))}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
            {Object.entries(DOMAIN_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </div>

        {/* Mode-specific inputs */}
        <div className="flex items-end gap-2 flex-wrap">
          {(mode === 'best-sellers' || mode === 'product-finder') && (
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Category ID</label>
              <input type="text" value={categoryId} onChange={e => setCategoryId(e.target.value)}
                placeholder="e.g. 172282"
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-28" />
            </div>
          )}
          {mode === 'product-finder' && (<>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">BSR Max</label>
              <input type="number" value={bsrMax} onChange={e => setBsrMax(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-24" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Price $</label>
              <div className="flex gap-1">
                <input type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} placeholder="min"
                  className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-16" />
                <input type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} placeholder="max"
                  className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-16" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Reviews Min</label>
              <input type="number" value={reviewMin} onChange={e => setReviewMin(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-20" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Monthly Sold Min</label>
              <input type="number" value={monthlySoldMin} onChange={e => setMonthlySoldMin(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-20" />
            </div>
          </>)}
          {mode === 'seller' && (
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Seller ID</label>
              <input type="text" value={sellerId} onChange={e => setSellerId(e.target.value)}
                placeholder="e.g. A1B2C3D4E5"
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs w-40" />
            </div>
          )}
          <button onClick={handleSearch} disabled={loading}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {mode === 'best-sellers' && (
          <p className="text-[10px] text-gray-400">
            Cost: ~1 token (best sellers) + ~4 tokens/ASIN (preview). Find category IDs on Amazon URL or via Keepa.
          </p>
        )}
        {mode === 'product-finder' && (
          <p className="text-[10px] text-gray-400">Cost: 1 token (search) + ~4 tokens/ASIN (preview).</p>
        )}
        {mode === 'seller' && (
          <p className="text-[10px] text-gray-400">Cost: 1 token (seller lookup) + ~4 tokens/ASIN (preview).</p>
        )}
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}

      {/* Results */}
      {results.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white">
          {/* Bulk actions bar */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
            <div className="flex items-center gap-2">
              <button onClick={selectAllUntracked}
                className="text-xs text-blue-600 hover:text-blue-800">Select all untracked</button>
              <span className="text-xs text-gray-400">{selected.size} selected of {results.length}</span>
            </div>
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <select value={trackLabel} onChange={e => setTrackLabel(e.target.value as 'competitor' | 'research')}
                  className="rounded border border-gray-200 px-2 py-1 text-xs">
                  <option value="research">Research</option>
                  <option value="competitor">Competitor</option>
                </select>
                <button onClick={bulkTrack} disabled={loading}
                  className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50">
                  Track {selected.size} ASINs
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">BSR</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Rating</th>
                  <th className="px-3 py-2 text-right">Reviews</th>
                  <th className="px-3 py-2 text-right">Monthly Sold</th>
                  <th className="px-3 py-2 text-right">Sellers</th>
                  <th className="px-3 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {results.map(p => (
                  <tr key={p.asin} className={`hover:bg-gray-50 ${p.tracked?.is_active ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox"
                        checked={selected.has(p.asin)}
                        onChange={() => toggleSelect(p.asin)}
                        disabled={!!p.tracked?.is_active}
                        className="rounded border-gray-300" />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {p.imageUrl && <img src={p.imageUrl} alt="" className="h-10 w-10 rounded object-contain bg-gray-50" />}
                        <div className="min-w-0">
                          <div className="truncate max-w-[250px] font-medium text-gray-900" title={p.title ?? ''}>{p.title ?? p.asin}</div>
                          <div className="text-[10px] text-gray-400">
                            {p.asin} {p.brand && <span>· {p.brand}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.salesRank)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(p.buyBoxPrice ?? p.price)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {p.rating != null ? <span>{p.rating.toFixed(1)}</span> : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.reviewCount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.monthlySold)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.newOfferCount)}</td>
                    <td className="px-3 py-2 text-center">
                      {p.tracked?.is_active ? (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          p.tracked.label === 'own' ? 'bg-blue-100 text-blue-700'
                            : p.tracked.label === 'competitor' ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>{p.tracked.label}</span>
                      ) : (
                        <span className="text-[10px] text-gray-300">new</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
